import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Tooltip,
  Typography,
  createTheme,
  ThemeProvider,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  DocumentScanner as DocumentScannerIcon,
} from '@mui/icons-material';
import {
  DataGridPro,
  GridRowModes,
  koKR,
} from '@mui/x-data-grid-pro';
import { v4 as uuidv4 } from 'uuid';
import { gcoreServerUrl } from '../../config';
import { csrfFetch } from '../../store/csrf';
import * as gtestComparisonActions from '../../store/gtestComparison';
import * as gstandardActions from '../../store/gstandard';
import { AlertDialog } from '../dialog';
import { CustomNoRowsOverlay } from '../datagrid';
import {
  GTestLeadFields,
  areLeadsReady,
  buildClassificationOptions,
  buildLeadJsonData,
  collectLeadKeys,
  joinClassificationParts,
} from '../GTestComparison';

const PDF_ACCEPT = '.pdf,application/pdf';

const isPdfFile = (f) => {
  if (!f) return false;
  if (f.type && f.type === 'application/pdf') return true;
  return Boolean(f.name?.toLowerCase().endsWith('.pdf'));
};

/**
 * ADMIN — 성적서 1파일 임시 추출 테스트 (마스터 DB 저장 없음)
 * 선행값 UX는 부자재 서류등록 시험성과대비표와 동일
 */
const AdminTestExtractManagement = () => {
  const sessionUser = useSelector((state) => state.session.sessionUser);
  const [standards, setStandards] = useState([]);
  const [standard, setStandard] = useState(null);
  const [standardDetail, setStandardDetail] = useState(null);
  const [leadKeys, setLeadKeys] = useState([]);
  const [leadData, setLeadData] = useState({});
  const [classificationParts, setClassificationParts] = useState([]);
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [extractType, setExtractType] = useState('');
  const [diagnostics, setDiagnostics] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [alertInfo, setAlertInfo] = useState({});

  const applySelectedFile = useCallback((nextFile) => {
    if (!nextFile) return;
    if (!isPdfFile(nextFile)) {
      setAlertInfo({
        titleAlert: '안내',
        messageAlert: 'PDF 파일만 선택할 수 있습니다.',
        open: true,
      });
      return;
    }
    setFile(nextFile);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await csrfFetch(null, `${gcoreServerUrl}/api/gstandards`);
        const data = await response.json();
        if (!cancelled) setStandards(data?.gstandards || []);
      } catch (_e) {
        if (!cancelled) setStandards([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!standard?.id) {
      setStandardDetail(null);
      setLeadKeys([]);
      setClassificationParts([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const [detail, comparisons] = await Promise.all([
          gstandardActions.selectDirect(standard.id),
          gtestComparisonActions.selectAllByGstandardIdDirect(standard.id, { evaluation: true }),
        ]);
        if (cancelled) return;
        setStandardDetail(detail || standard);
        setLeadKeys(collectLeadKeys(comparisons));
        const comps = detail?.classificationComponents;
        setClassificationParts(
          Array.isArray(comps) && comps.length
            ? comps.map(() => '')
            : [],
        );
      } catch (err) {
        if (!cancelled) {
          setAlertInfo({
            titleAlert: '안내',
            messageAlert: err?.serverResponse?.message || err.message || '표준 정보를 불러오지 못했습니다.',
            open: true,
          });
          setStandardDetail(null);
          setLeadKeys([]);
          setClassificationParts([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [standard?.id]);

  const classificationOptions = useMemo(
    () => buildClassificationOptions(standardDetail),
    [standardDetail],
  );

  const leadsReady = useMemo(
    () => areLeadsReady(leadKeys, leadData),
    [leadKeys, leadData],
  );

  const canAnalyze = Boolean(file && standard?.id && leadsReady && !busy);

  const updateLead = (key, value) => {
    setLeadData((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'classification' && !classificationOptions.length) {
        next.classificationType0 = value || '';
      }
      return next;
    });
  };

  const handleClassificationPartChange = (colIdx, value) => {
    setClassificationParts((prev) => {
      const next = [...prev];
      next[colIdx] = value;
      const joined = joinClassificationParts(next);
      setLeadData((ld) => ({
        ...ld,
        classification: joined || '',
        classificationType0: next[0] || '',
        classificationParts: next,
      }));
      return next;
    });
  };

  const columns = useMemo(() => [
    { field: 'item', headerName: '시험항목', flex: 1.2, minWidth: 140, editable: true },
    { field: 'unit', headerName: '단위', width: 90, editable: true },
    { field: 'criterion', headerName: '기준', flex: 1.4, minWidth: 160, editable: true },
    { field: 'value', headerName: '시험값', width: 110, editable: true },
    { field: 'result', headerName: '판정', width: 90, editable: true },
    { field: 'method', headerName: '시험방법', flex: 1, minWidth: 120, editable: true },
    { field: 'office', headerName: '기관', width: 80, editable: true },
  ], []);

  const processRowUpdate = (newRow) => {
    setRows((prev) => prev.map((r) => (r.id === newRow.id ? newRow : r)));
    return newRow;
  };

  const handleAnalyze = async () => {
    if (!file) {
      setAlertInfo({ titleAlert: '안내', messageAlert: '성적서 PDF를 선택하세요.', open: true });
      return;
    }
    if (!standard?.id) {
      setAlertInfo({
        titleAlert: '안내',
        messageAlert: '먼저 표준번호를 선택하세요. 표준을 기준으로 선행 입력란과 평가 기준이 결정됩니다.',
        open: true,
      });
      return;
    }
    if (!leadsReady) {
      setAlertInfo({
        titleAlert: '안내',
        messageAlert: '선행값(두께·등급 등)을 모두 입력한 뒤 성적서를 분석할 수 있습니다.',
        open: true,
      });
      return;
    }
    setBusy(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('gstandardId', standard.id);
      formData.append('jsonData', JSON.stringify(buildLeadJsonData(leadKeys, leadData)));

      const response = await csrfFetch(null, `${gcoreServerUrl}/api/admin/testExtract`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      const result = data.testResult || {};
      const list = (result.g04docuGTestComparisons || []).map((row) => ({
        ...row,
        id: row.id || uuidv4(),
      }));
      setRows(list);
      setExtractType(result.extractType || '');
      setDiagnostics(result.diagnostics || null);
      if (!list.length) {
        setAlertInfo({
          titleAlert: '안내',
          messageAlert: result.diagnostics?.failReason
            || '분석 결과가 없습니다. 표준·선행값·PDF를 확인해 주세요.',
          open: true,
        });
      }
    } catch (err) {
      setRows([]);
      setExtractType('');
      setDiagnostics(null);
      setAlertInfo({
        titleAlert: '안내',
        messageAlert: err?.serverResponse?.message || err?.message || '분석에 실패했습니다.',
        open: true,
      });
    } finally {
      setBusy(false);
    }
  };

  if (!sessionUser) {
    return <Navigate to="/glass/signin" replace />;
  }
  if (sessionUser.type !== 'ADMIN') {
    return <Navigate to="/" replace />;
  }

  return (
    <ThemeProvider theme={createTheme()}>
      <Container component="main" maxWidth={false} sx={{ py: 2 }}>
        <Typography variant="h5" sx={{ mb: 0.5 }}>성적서 추출 테스트</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          ADMIN 전용. PDF를 임시로 분석합니다. 선행값 입력은 부자재 서류등록 시험성과대비표와 동일하며,
          결과 그리드는 화면에만 남고 마스터·서류 폴더에 저장되지 않습니다.
        </Typography>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Box
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDragOver(false);
              if (busy) return;
              applySelectedFile(e.dataTransfer.files?.[0]);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!busy) setDragOver(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDragOver(false);
            }}
            sx={{
              border: 2,
              borderStyle: 'dashed',
              borderColor: dragOver ? 'primary.main' : 'divider',
              borderRadius: 1,
              bgcolor: dragOver ? 'action.hover' : 'grey.50',
              p: 2,
              mb: 1.5,
              textAlign: 'center',
              opacity: busy ? 0.6 : 1,
              pointerEvents: busy ? 'none' : 'auto',
              transition: 'border-color 0.15s, background-color 0.15s',
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 36, color: 'text.secondary', mb: 0.5 }} />
            <Typography variant="body2" gutterBottom>
              PDF를 여기로 끌어다 놓거나
            </Typography>
            <Button variant="outlined" component="label" size="small" disabled={busy}>
              파일 선택
              <input
                type="file"
                accept={PDF_ACCEPT}
                hidden
                onChange={(e) => {
                  applySelectedFile(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {file?.name || '선택된 파일 없음'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 1.5 }}>
            <Autocomplete
              sx={{ minWidth: 320, flex: 1 }}
              options={standards}
              value={standard}
              onChange={(_e, v) => {
                setStandard(v);
                setLeadData({});
                setClassificationParts([]);
              }}
              getOptionLabel={(o) => {
                if (!o) return '';
                const no = o.no || o.code || '';
                return no ? `${no} — ${o.name || ''}` : (o.name || o.id || '');
              }}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              renderInput={(params) => (
                <TextField {...params} label="표준번호" size="small" required />
              )}
            />
            <Tooltip
              title={
                !file
                  ? '먼저 성적서 PDF를 선택하세요.'
                  : !standard?.id
                    ? '먼저 표준번호를 선택하세요.'
                    : !leadsReady
                      ? '선행값(두께·등급 등)을 모두 입력해야 분석할 수 있습니다.'
                      : '성적서를 분석하여 시험성과대비표 목록을 구성합니다. (QR 우선)'
              }
            >
              <span>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<DocumentScannerIcon />}
                  onClick={handleAnalyze}
                  disabled={!canAnalyze}
                  sx={{ height: 40 }}
                >
                  성적서 분석
                </Button>
              </span>
            </Tooltip>
          </Box>

          <GTestLeadFields
            hasStandard={Boolean(standard?.id)}
            leadKeys={leadKeys}
            leadData={leadData}
            onLeadChange={updateLead}
            classificationParts={classificationParts}
            onClassificationPartChange={handleClassificationPartChange}
            classificationOptions={classificationOptions}
            disabled={busy}
          />

          {(extractType || diagnostics) && (
            <Box sx={{ mt: 1.5 }}>
              {extractType && (
                <Typography variant="caption" color="text.secondary" display="block">
                  extractType: {extractType}
                </Typography>
              )}
              {diagnostics && (
                <Typography
                  variant="caption"
                  component="pre"
                  sx={{
                    mt: 0.5,
                    p: 1,
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    overflow: 'auto',
                    fontFamily: 'ui-monospace, monospace',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {JSON.stringify(diagnostics, null, 2)}
                </Typography>
              )}
            </Box>
          )}
        </Paper>

        <Paper sx={{ height: 520, p: 1 }}>
          <DataGridPro
            rows={rows}
            columns={columns}
            getRowId={(r) => r.id}
            editMode="row"
            rowModesModel={rowModesModel}
            onRowModesModelChange={setRowModesModel}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={() => {}}
            slots={{ noRowsOverlay: CustomNoRowsOverlay }}
            localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
            disableRowSelectionOnClick
            onRowDoubleClick={(params) => {
              setRowModesModel((m) => ({
                ...m,
                [params.id]: { mode: GridRowModes.Edit },
              }));
            }}
          />
        </Paper>

        <AlertDialog
          alertInfo={alertInfo}
          setAlertInfo={setAlertInfo}
        />
      </Container>
    </ThemeProvider>
  );
};

export default AdminTestExtractManagement;
